import * as React from 'react';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Select } from 'radix-ui';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag?: string;
}


const LANGUAGES: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  { code: 'my', name: 'Myanmar', nativeName: 'မြန်မာ', flag: '🇲🇲' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  {
    code: 'id',
    name: 'Indonesian',
    nativeName: 'Bahasa Indonesia',
    flag: '🇮🇩',
  },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'km', name: 'Cambodia', nativeName: 'ខ្មែរ', flag: '🇰🇭' },
  { code: 'de', name: 'Germany', nativeName: 'Deutsch', flag: '🇩🇪' },
];

interface LanguageSelectionProps {
  loadFlow: (lang: string) => void | Promise<void>;
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const LanguageSelection = React.forwardRef<
  HTMLButtonElement,
  LanguageSelectionProps
>((props, ref) => {
  const {
    value,
    onValueChange,
    placeholder = 'Select a language...',
    disabled,
    loadFlow,
  } = props;

  const selectedLanguage = [...LANGUAGES].find((lang) => lang.code === value);

  const prevSelectedLanguage = React.useRef<string | undefined>(value);

  React.useEffect(() => {
    if (prevSelectedLanguage.current != selectedLanguage?.code) {
      loadFlow(selectedLanguage?.code ?? 'en');
    }
    prevSelectedLanguage.current = selectedLanguage?.code;
  }, [selectedLanguage?.code]);

  return (
    <Select.Root
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
    >
      <Select.Trigger
        ref={ref}
        className={
          'flex h-10 w-full items-center justify-between rounded-md border border-input bg-inherit px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
        }
        aria-label="Language selection"
      >
        <Select.Value placeholder={placeholder}>
          {selectedLanguage && (
            <div className="flex items-center gap-2">
              {selectedLanguage.flag && (
                <span className="text-lg">{selectedLanguage.flag}</span>
              )}
              <span>{selectedLanguage.nativeName}</span>
              <span className="text-sm text-muted-foreground">
                ({selectedLanguage.code.toUpperCase()})
              </span>
            </div>
          )}
        </Select.Value>
        <Select.Icon className="opacity-50">
          <ChevronDown />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content className="relative max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
          <Select.ScrollUpButton className="flex cursor-default items-center justify-center py-1">
            <ChevronUp />
          </Select.ScrollUpButton>
          <Select.Viewport className="p-1">
            <Select.Group>
              <Select.Label className="py-1.5 pl-8 pr-2 text-sm font-semibold">
                Languages
              </Select.Label>
              {LANGUAGES.map((language) => (
                <Select.Item
                  key={language.code}
                  value={language.code}
                  className="relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                >
                  <Select.ItemText>
                    <div className="flex items-center gap-2">
                      {language.flag && (
                        <span className="text-lg">{language.flag}</span>
                      )}
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {language.nativeName}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {language.name}
                        </span>
                      </div>
                    </div>
                  </Select.ItemText>
                  <Select.ItemIndicator className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    <Check className="h-4 w-4" />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Group>

            <Select.Separator className="-mx-1 my-1 h-px bg-muted" />
          </Select.Viewport>
          <Select.ScrollDownButton className="flex cursor-default items-center justify-center py-1">
            <ChevronDown />
          </Select.ScrollDownButton>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
});

LanguageSelection.displayName = 'LanguageSelection';

export { LanguageSelection };
